// client/src/components/secure/AgentInvitesPanel.js — "Invite to the app" (managing broker).
//
// Shown on Agents and on Login Accounts. Each active agent, whether they can sign in, and the
// state of their invite (invited / accepted / expired / cancelled). "Invite to the app" makes the
// agent's login and a one-time setup link (7 days, works once). The link is shown here once, to
// copy; "Email the invite" sends it only when clicked (routes/agentInvites.js). "Invite all agents
// without a login" makes the links for everyone at once and sends nothing.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../shared/DataList';

const STATE = {
    no_login: { label: 'No login', tone: 'bg-slate-100 text-slate-700' },
    invited: { label: 'Invited', tone: 'bg-indigo-100 text-indigo-800' },
    expired: { label: 'Invite expired', tone: 'bg-amber-100 text-amber-800' },
    cancelled: { label: 'Invite cancelled', tone: 'bg-slate-200 text-slate-700' },
    replaced: { label: 'Invite replaced', tone: 'bg-slate-200 text-slate-700' },
    active: { label: 'Can sign in', tone: 'bg-green-100 text-green-800' },
    temporary_password: { label: 'Temporary password, not yet used', tone: 'bg-amber-100 text-amber-800' },
    revoked: { label: 'Login revoked', tone: 'bg-red-100 text-red-800' },
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; } catch (e) { return false; }
};

export default function AgentInvitesPanel({ compact = false }) {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [busy, setBusy] = useState(false);
    const [issued, setIssued] = useState(null);   // one invite just made: { link, token?, ... }
    const [bulk, setBulk] = useState(null);       // the bulk result
    const [nameFor, setNameFor] = useState({});   // agent_id -> username override

    const load = useCallback(() => {
        api.get('/api/agent-invites').then((r) => { setData(r); setError(''); }).catch((e) => setError(e.message));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const note = (m) => { setFlash(m); setTimeout(() => setFlash(''), 8000); };
    const run = async (fn) => {
        setBusy(true); setError('');
        try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const invite = (a) => run(async () => {
        const body = { agent_id: a.agent_id };
        if (!a.user_id && nameFor[a.agent_id]) body.username = nameFor[a.agent_id];
        const r = await api.post('/api/agent-invites', body);
        setIssued({ ...r, email: a.email });
        load();
    });
    const emailIt = (inviteId, link, agent) => run(async () => {
        // The link on screen carries its token after "token="; sending it back lets the server
        // e-mail that same link (it keeps only a hash, so it checks it matches).
        const token = link ? (link.split('token=')[1] || '') : undefined;
        if (!window.confirm('Send the invite e-mail to this agent now?')) return;
        const r = await api.post(`/api/agent-invites/${inviteId}/email`, token ? { token } : {});
        note(r.message);
        if (r.link) {
            setIssued((cur) => (cur ? { ...cur, invite_id: r.invite_id, link: r.link, expires_at: r.expires_at }
                : agent ? { name: agent.name, username: agent.username, email: agent.email, invite_id: r.invite_id, link: r.link, expires_at: r.expires_at } : cur));
        }
        load();
    });
    const cancel = (a) => run(async () => {
        if (!window.confirm(`Cancel the invite for ${a.name}? The link stops working and the unused login is switched off. You can invite them again later.`)) return;
        const r = await api.post(`/api/agent-invites/${a.invite.invite_id}/cancel`, {});
        note(r.message); load();
    });
    const inviteAll = () => run(async () => {
        if (!window.confirm('Make an invite link for every active agent without a login? Nothing is e-mailed; you hand the links out.')) return;
        const r = await api.post('/api/agent-invites/bulk', {});
        setBulk(r); note(r.message); load();
    });

    if (error && !data) return <div className="px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>;
    if (!data) return <div className="text-sm text-slate-500">Loading agents…</div>;

    const agents = data.agents || [];
    const withoutLogin = agents.filter((a) => a.state === 'no_login').length;
    const btn = 'text-xs px-2.5 py-1 rounded-md border';

    return (
        <div className="border border-slate-200 bg-white rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-base font-semibold text-slate-800">Invite agents to the app</h2>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">
                        An invite makes the agent's login and a one-time link where they choose their own password (good
                        for {data.valid_days} days, works once). Nobody else ever knows the password. Copy the link, or e-mail it
                        from here — nothing is sent unless you click "Email the invite". Their login opens only their own
                        clients, deals and listings, plus the property and zoning searches.
                    </p>
                </div>
                {withoutLogin > 0 && data.installed && (
                    <button disabled={busy} onClick={inviteAll}
                        className="text-sm px-3 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-slate-400">
                        Invite all agents without a login ({withoutLogin})
                    </button>
                )}
            </div>

            {!data.installed && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    Invites are not installed on this site yet (the 2026-10-24 agent invites update). Until then, use "+ New login" on Login Accounts.
                </p>
            )}
            {data.two_factor_required && (
                <p className="text-xs text-slate-600">Two-factor sign-in is required for every login, so each agent will be asked to set it up (a phone authenticator app) the first time they sign in.</p>
            )}
            {!data.mail_configured && (
                <p className="text-xs text-slate-500">E-mail is not set up on this site, so copy each link and send it yourself.</p>
            )}
            {error && <div className="px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}
            {flash && <div className="px-3 py-2 rounded border border-green-200 bg-green-50 text-sm text-green-700">{flash}</div>}

            {issued && (
                <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between gap-2">
                        <p className="text-sm font-semibold text-indigo-900">Invite for {issued.name} — username <span className="font-mono">{issued.username}</span></p>
                        <button onClick={() => setIssued(null)} className="text-xs text-indigo-700 hover:underline">Close</button>
                    </div>
                    <p className="text-xs text-indigo-900">This link is shown only now. It works once, until {fmt(issued.expires_at)}.</p>
                    <div className="flex flex-wrap gap-2 items-center">
                        <input readOnly value={issued.link} onFocus={(e) => e.target.select()}
                            className="flex-1 min-w-[16rem] font-mono text-xs border border-indigo-200 rounded px-2 py-1.5 bg-white" />
                        <button className={`${btn} bg-white border-indigo-300`} onClick={async () => note((await copy(issued.link)) ? 'Link copied.' : 'Select the link and copy it.')}>Copy link</button>
                        {data.mail_configured && issued.email && (
                            <button disabled={busy} className={`${btn} bg-indigo-700 text-white border-indigo-700`} onClick={() => emailIt(issued.invite_id, issued.link)}>
                                Email the invite to {issued.email}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {bulk && bulk.invited && bulk.invited.length > 0 && (
                <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between gap-2">
                        <p className="text-sm font-semibold text-indigo-900">{bulk.invited.length} invite links — shown only now; nothing was sent</p>
                        <button onClick={() => setBulk(null)} className="text-xs text-indigo-700 hover:underline">Close</button>
                    </div>
                    <textarea readOnly rows={Math.min(10, bulk.invited.length + 1)} onFocus={(e) => e.target.select()}
                        className="w-full font-mono text-xs border border-indigo-200 rounded px-2 py-1.5 bg-white"
                        value={bulk.invited.map((i) => `${i.name} (${i.username}): ${i.link}`).join('\n')} />
                    <button className={`${btn} bg-white border-indigo-300`}
                        onClick={async () => note((await copy(bulk.invited.map((i) => `${i.name} (${i.username}): ${i.link}`).join('\n'))) ? 'All links copied.' : 'Select the list and copy it.')}>Copy all</button>
                    {bulk.problems && bulk.problems.length > 0 && (
                        <ul className="text-xs text-red-700 list-disc ml-4">{bulk.problems.map((p) => <li key={p.agent_id}>{p.name}: {p.message}</li>)}</ul>
                    )}
                </div>
            )}

            <DataList rows={agents} rowKey="agent_id" csvName="agent-invites" defaultSort={{ key: 'name', dir: 'asc' }} empty="No active agents."
                filters={compact ? [] : [{ key: 'state_label', label: 'Status' }]}
                columns={[
                    { key: 'name', label: 'Agent', render: (a) => <span className="font-medium">{a.name}{a.is_managing_broker ? <span className="ml-1 text-xs text-slate-500">(managing broker)</span> : null}</span> },
                    { key: 'email', label: 'E-mail', render: (a) => a.email || <span className="text-xs text-amber-700">none on file</span> },
                    { key: 'username', label: 'Login', value: (a) => a.username || a.suggested_username || '',
                        render: (a) => (a.username ? <span className="font-mono text-xs">{a.username}</span>
                            : data.installed ? <input aria-label={`Username for ${a.name}`} className="font-mono text-xs border border-slate-300 rounded px-1.5 py-1 w-40"
                                placeholder={a.suggested_username} value={nameFor[a.agent_id] || ''}
                                onChange={(e) => setNameFor((m) => ({ ...m, [a.agent_id]: e.target.value }))} /> : '—') },
                    { key: 'state_label', label: 'Status', value: (a) => (STATE[a.state] || {}).label || a.state,
                        render: (a) => (
                            <span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${(STATE[a.state] || {}).tone || ''}`}>{(STATE[a.state] || {}).label || a.state}</span>
                                {a.invite && a.state === 'invited' && <span className="block text-[11px] text-slate-500 mt-0.5">until {fmt(a.invite.expires_at)}{a.invite.emailed_at ? ` · e-mailed ${fmt(a.invite.emailed_at)}` : ''}</span>}
                                {a.invite && a.invite.accepted_at && <span className="block text-[11px] text-slate-500 mt-0.5">accepted {fmt(a.invite.accepted_at)}</span>}
                            </span>) },
                    { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (a) => (!data.installed ? null : (
                        <span className="flex flex-wrap gap-1.5">
                            {a.state === 'no_login' && <button disabled={busy} className={`${btn} bg-slate-800 text-white border-slate-800`} onClick={() => invite(a)}>Invite to the app</button>}
                            {['invited', 'expired', 'cancelled', 'replaced'].includes(a.state) && <button disabled={busy} className={`${btn} border-slate-300`} onClick={() => invite(a)}>{a.state === 'invited' ? 'New link' : 'Invite again'}</button>}
                            {a.state === 'invited' && data.mail_configured && a.email && <button disabled={busy} className={`${btn} border-slate-300`} onClick={() => emailIt(a.invite.invite_id, null, a)} title="Makes a fresh link and e-mails it; any link copied before stops working">Email the invite</button>}
                            {a.state === 'invited' && <button disabled={busy} className={`${btn} border-red-200 text-red-700`} onClick={() => cancel(a)}>Cancel</button>}
                        </span>)) },
                ]} />
        </div>
    );
}
